import { createApi } from '@reduxjs/toolkit/query/react';
import type { TPaginationResult } from '../api-common.types';
import config from 'config/index';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import { EUserType } from 'types/accessControll.types';
import type { AccessGroup, TClient } from 'types/client.types';

export type TCreateClientContract = {
  startDate: string;
  endDate: string;
  contractSigner: {
    firstName: string;
    lastName: string;
  };
  quotas: {
    interviews: number;
    interviewsConsumedWhen: 'engaged' | 'started' | 'created';
  };
  pilot?: {
    endDate: string;
  };
};

export type TCreateClientRequest = {
  orgName: string;
  customerSuccessManagerId: string;
  contract?: TCreateClientContract;
};

const reducerPath = 'clients' as const;
const path = 'v1/clients' as const;

export type TSmsTemplate = {
  id: string;
  name: string;
};

export type TClientUser = {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  flag: string;
  isAdmin: boolean;
  latest_activity_at?: Date;
  latest_login_at?: Date;
};

export type TFindPaginatedClientsRequest = {
  pageIndex: number;
  pageSize: number;
  filter: {
    clientId: string;
    text: string;
    myClients: boolean;
  };
};

export const clientsApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  tagTypes: ['Clients'],
  endpoints: (builder) => ({
    findPaginatedClients: builder.query<TPaginationResult<TClient>, TFindPaginatedClientsRequest>({
      query: (payload) => {
        console.log('findPaginatedClients payload', payload);
        return {
          url: `/paginated-find`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
      providesTags: ['Clients'],
    }),

    getAllClients: builder.query<{ clients: TClient[] }, void>({
      query: () => ({
        url: `/get-all`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
    }),

    getClientById: builder.query<{ client: TClient }, string>({
      query: (clientId) => ({
        url: `/${clientId}`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
    }),

    getClientUsers: builder.query<{ users: TClientUser[] }, { clientId: string }>({
      query: (x) => ({
        url: `/${x.clientId}/users`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
    }),

    getClientSmsTemplates: builder.query<{ templates: TSmsTemplate[] }, { clientId: string }>({
      query: (payload) => ({
        url: `/${payload.clientId}/sms-templates`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
    }),

    getClientAccessGroups: builder.query<{ accessGroups: AccessGroup[] }, { clientId: string }>({
      query: (payload) => ({
        url: `/${payload.clientId}/access-groups`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
    }),

    createClient: builder.mutation<{ client: TClient }, TCreateClientRequest>({
      query: (payload) => ({
        url: `/create`,
        method: 'POST',
        data: payload,
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
      invalidatesTags: ['Clients'],
    }),

    loginAsUser: builder.mutation<{ grant: string }, { clientId: string; userId: string }>({
      query: ({ clientId, userId }) => ({
        url: `/${clientId}/users/${userId}/login-as`,
        method: 'POST',
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
    }),
  }),
});

export const {
  useGetAllClientsQuery,
  useGetClientByIdQuery,
  useGetClientUsersQuery,
  useGetClientSmsTemplatesQuery,
  useGetClientAccessGroupsQuery,
  useLazyGetClientAccessGroupsQuery,
  useFindPaginatedClientsQuery,
  useCreateClientMutation,
  useLoginAsUserMutation,
} = clientsApi;
